import { useEffect, useMemo, useRef, useState } from "react";
import { INTAKE_MAX_PHOTOS, INTAKE_Q, type IntakeQuestion } from "@klantpunt/shared";
import { Segmented } from "../../app/Choice";
import { compressPhoto } from "../../lib/photos";
import { Choices } from "../issues/IssueForm";

type Answers = Partial<Record<IntakeQuestion, string>>;

/** Versturen als multipart (foto's mee); geeft het meldnummer, of null als de server het stil negeerde. */
async function send(url: string, body: FormData): Promise<string | null> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: "POST",
      body,
      credentials: "same-origin",
      headers: { accept: "application/json" },
    });
  } catch {
    throw new Error("Geen verbinding. Probeer het opnieuw als je weer online bent.");
  }
  const data = (await res.json().catch(() => ({}))) as { code?: string | null; message?: string };
  if (!res.ok) throw new Error(data.message ?? "Versturen mislukt");
  return data.code ?? null;
}

/**
 * Meldscherm na het scannen van een asset-QR (docs/MELDINGEN.md §3): een paar vragen in gewone taal, foto
 * verplicht met "Foto maken lukt niet", contact optioneel. Zonder inloggen ook bewoner of leverancier.
 */
export function IntakeForm({
  url,
  publicForm,
  onDone,
  onCancel,
}: {
  url: string;
  /** Zonder inloggen: wie meldt er en contactgegevens. */
  publicForm: boolean;
  onDone: (code: string | null) => void;
  onCancel: () => void;
}) {
  const [a, setA] = useState<Answers>({});
  const [photos, setPhotos] = useState<File[]>([]);
  const [noPhoto, setNoPhoto] = useState(false);
  const [description, setDescription] = useState("");
  const [reporter, setReporter] = useState<"bewoner" | "leverancier">("bewoner");
  const [contact, setContact] = useState({ name: "", email: "", phone: "" });
  const [website, setWebsite] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(Date.now());
  const input = useRef<HTMLInputElement>(null);
  const previews = useMemo(() => photos.map((f) => URL.createObjectURL(f)), [photos]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  const missing = [
    !a.what && "wat er aan de hand is",
    !photos.length && !noPhoto && "een foto",
    !a.danger && "of het gevaarlijk is",
    !a.usable && "of het nog gebruikt kan worden",
    !a.since && "sinds wanneer",
    a.what === "anders" && !description.trim() && "een korte beschrijving",
  ].filter(Boolean) as string[];

  async function submit() {
    if (missing.length) return;
    setBusy(true);
    setError(null);
    const body = new FormData();
    for (const [k, v] of Object.entries(a)) body.append(k, v);
    body.append("description", description);
    body.append("noPhoto", String(noPhoto && !photos.length));
    body.append("elapsed", String(Date.now() - started.current));
    if (publicForm) {
      body.append("reporter", reporter);
      body.append("name", contact.name);
      body.append("email", contact.email);
      body.append("phone", contact.phone);
      body.append("website", website);
    }
    try {
      for (const [i, f] of photos.entries()) body.append("photo", await compressPhoto(f), `foto-${i + 1}.jpg`);
      onDone(await send(url, body));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Versturen mislukt");
      setBusy(false);
    }
  }

  const pick = (q: IntakeQuestion) => (k: string) => setA({ ...a, [q]: k });

  return (
    <form
      className="intakeform stack"
      aria-busy={busy}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      {publicForm && (
        <Segmented
          label="Ik ben"
          value={reporter}
          onChange={setReporter}
          items={[
            { value: "bewoner", label: "Bewoner of gebruiker" },
            { value: "leverancier", label: "Leverancier" },
          ]}
        />
      )}
      <Choices label={INTAKE_Q.what.q} required value={a.what} opts={INTAKE_Q.what.opts} onPick={pick("what")} />

      <div className="q">
        <span className="ql">Foto *</span>
        <div className="photos">
          {previews.map((src, i) => (
            <div className="ph" key={src}>
              <img src={src} alt={`Foto ${i + 1}`} />
              <button
                type="button"
                className="phremove"
                aria-label={`Foto ${i + 1} weghalen`}
                onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
              >
                ×
              </button>
            </div>
          ))}
          {photos.length < INTAKE_MAX_PHOTOS && (
            <button type="button" className="add" onClick={() => input.current?.click()}>
              +<br />
              Foto
            </button>
          )}
        </div>
        <input
          ref={input}
          type="file"
          accept="image/jpeg,image/png"
          multiple
          hidden
          data-testid="intake-photo"
          onChange={(e) => {
            const add = [...(e.target.files ?? [])];
            setPhotos([...photos, ...add].slice(0, INTAKE_MAX_PHOTOS));
            setNoPhoto(false);
            e.target.value = "";
          }}
        />
        {!photos.length &&
          (noPhoto ? (
            <p className="hint warn">
              Beschrijf het dan extra goed; de servicedesk komt misschien kijken.{" "}
              <button type="button" className="linkbtn" onClick={() => input.current?.click()}>
                Toch foto
              </button>
            </p>
          ) : (
            <button type="button" className="linkbtn" onClick={() => setNoPhoto(true)}>
              Foto maken lukt niet
            </button>
          ))}
      </div>

      <Choices
        label={INTAKE_Q.danger.q}
        required
        value={a.danger}
        opts={INTAKE_Q.danger.opts}
        onPick={pick("danger")}
      />
      {a.danger === "ja" && (
        <div className="alert" role="alert">
          Bij direct gevaar: zorg dat niemand in de buurt komt en bel <a href="tel:112">112</a>.
        </div>
      )}
      <Choices
        label={INTAKE_Q.usable.q}
        required
        value={a.usable}
        opts={INTAKE_Q.usable.opts}
        onPick={pick("usable")}
      />
      <Choices label={INTAKE_Q.since.q} required value={a.since} opts={INTAKE_Q.since.opts} onPick={pick("since")} />

      <label className="field">
        <span>Toelichting{a.what === "anders" ? " *" : " (optioneel)"}</span>
        <textarea
          rows={3}
          maxLength={2000}
          value={description}
          placeholder="Bijvoorbeeld: druppelt onder de ketel, plas op de vloer"
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>

      {publicForm && (
        <fieldset className="q">
          <legend className="ql">Contact voor terugkoppeling (optioneel)</legend>
          <div className="grid3">
            <label className="field">
              <span>Naam</span>
              <input
                value={contact.name}
                maxLength={120}
                autoComplete="name"
                onChange={(e) => setContact({ ...contact, name: e.target.value })}
              />
            </label>
            <label className="field">
              <span>E-mailadres</span>
              <input
                type="email"
                value={contact.email}
                maxLength={200}
                autoComplete="email"
                onChange={(e) => setContact({ ...contact, email: e.target.value })}
              />
            </label>
            <label className="field">
              <span>Telefoon</span>
              <input
                type="tel"
                value={contact.phone}
                maxLength={40}
                autoComplete="tel"
                onChange={(e) => setContact({ ...contact, phone: e.target.value })}
              />
            </label>
          </div>
          {/* Honeypot: mensen zien dit veld niet. */}
          <label className="hp" aria-hidden="true">
            Website
            <input tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} />
          </label>
        </fieldset>
      )}

      {error && (
        <div className="alert" role="alert">
          {error}
        </div>
      )}
      <div className="btnrow">
        <button type="submit" className="btn primary" disabled={busy || missing.length > 0}>
          {busy ? "Versturen…" : "Versturen"}
        </button>
        <button type="button" className="btn ghost" onClick={onCancel} disabled={busy}>
          Annuleren
        </button>
      </div>
      {missing.length > 0 && <p className="muted small">Nog nodig: {missing.join(", ")}.</p>}
    </form>
  );
}
